import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetActivePatients } from "api/services/admin/hooks";
import type { Professional } from "api/services/admin/schemas";
import { useProfileContext } from "contexts/ProfileContext";
import { ALL_PROFESSIONALS, type ProfessionalValue, useFilterOptions } from "routes/admin/hooks/useFilterOptions";
import type { BarConfig } from "shared/atoms/Charts";
import { StackedBarChart } from "shared/atoms/Charts";
import { MUISelect } from "shared/atoms/MUI";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout";
import { WidgetBox } from "shared/containers/WidgetBox";

import { NoGraphData } from "./NoGraphData";
import { fillMissingMonths, getStartTimeSixMonthsAgo } from "./utils";

interface ActivePatientsProps {
  hcoID: number;
  hcpID: number | undefined;
  professionals: Professional[];
}

export const ActivePatients: React.FC<ActivePatientsProps> = ({ hcoID, hcpID, professionals }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();
  const { professionalOptions } = useFilterOptions(professionals);
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalValue>(ALL_PROFESSIONALS);

  useEffect(() => {
    setSelectedProfessional(ALL_PROFESSIONALS);
  }, [hcpID]);

  invariant(profile, "Profile must be defined");

  const { data: activePatientsData, isLoading } = useGetActivePatients({
    health_care_organization_id: hcoID,
    params: {
      hcp_id: hcpID || undefined,
      start_time: getStartTimeSixMonthsAgo(),
      therapist_id: selectedProfessional === ALL_PROFESSIONALS ? undefined : selectedProfessional,
    },
  });
  const data = fillMissingMonths(activePatientsData?.items || [], ["total_hep", "total_rtm"]);

  const stackTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    data.forEach(item => {
      totals[item.month] = (item.total_hep || 0) + (item.total_rtm || 0);
    });
    return totals;
  }, [data]);

  const hasData = data.some(item => (item.total_hep || 0) > 0 || (item.total_rtm || 0) > 0);

  const bars: BarConfig[] = [
    { dataKey: "total_hep", name: t("invite.treatment_type_options.hep"), color: theme.colors.redesign.b100 },
    { dataKey: "total_rtm", name: t("invite.treatment_type_options.rtm"), color: theme.colors.redesign.b80 },
  ];

  return (
    <StyledWidgetBox
      title={t("admin.active_patients.title")}
      description={t("admin.active_patients.description")}
      icon={<FontAwesomeIcon icon={faUsers} />}
      toolbar={
        <MUISelect
          size="small"
          options={professionalOptions}
          disabled={isLoading}
          value={selectedProfessional}
          onChange={event => {
            setSelectedProfessional(event.target.value as ProfessionalValue);
          }}
        />
      }
      color="blue"
      skipPadding
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton />}
        {!isLoading && !hasData && <NoGraphData title={t("admin.active_patients.no_data")} />}
        {!isLoading && hasData && (
          <StackedBarChart
            data={data}
            bars={bars}
            tooltipProps={{
              color: theme.colors.redesign.b100,
              labelFormatter: (label: string) => format(parseISO(label), "MMMM yyyy"),
              stackTotals,
            }}
            height={240}
            xAxisDataKey="month"
            xAxisProps={{
              tickFormatter: value => format(parseISO(value), "MMM"),
            }}
            hideLegend
          />
        )}
      </Box>
    </StyledWidgetBox>
  );
};

const StyledWidgetBox = styled(WidgetBox)`
  height: 100%;
`;
